import React, { useState, useEffect, useMemo } from 'react';
import { Employee, ModalActionType, ShiftAssignment, EmployeeStatus } from '../types';
import { PlusCircleIcon } from '../constants';
import EmployeeTable from './EmployeeTable';
import EmployeeFormModal from './EmployeeFormModal';
import DeleteEmployeeModal from './DeleteEmployeeModal';
import HelpIcon from './HelpIcon';
import { useAppContext } from '../contexts/AppContext';

interface ProductionStatusData {
  currentShift: {
    number: number;
    isActive: boolean;
  };
}

type ShiftTab = 'SHIFT_1' | 'SHIFT_2';

const EmployeesPageDB: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState<ModalActionType | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeShiftTab, setActiveShiftTab] = useState<ShiftTab>('SHIFT_1');
  const [currentShift, setCurrentShift] = useState<number | null>(null);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const { isAppInitialized, apiUrl } = useAppContext();

  // Load employees from database on mount
  useEffect(() => {
    fetchEmployees();
  }, [apiUrl]);

  const fetchEmployees = async () => {
    try {
      setLoadingEmployees(true);
      const response = await fetch(`${apiUrl}/employees`);
      if (response.ok) {
        const data = await response.json();
        if (data.data && Array.isArray(data.data)) {
          setEmployees(data.data);
        } else if (data.message === 'success' && Array.isArray(data.data)) {
          setEmployees(data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoadingEmployees(false);
    }
  };

  // Effect to fetch current shift and set default tab
  useEffect(() => {
    if (!isAppInitialized) return;

    const fetchCurrentShift = async () => {
      try {
        const response = await fetch(`${apiUrl}/production-status`);
        const result = await response.json();
        
        if (result.message === 'success' && result.data?.currentShift) {
          const shiftNumber = result.data.currentShift.number;
          const isActive = result.data.currentShift.isActive;
          
          setCurrentShift(shiftNumber);
          
          // Only set default tab if shift is active and it's a valid shift number
          if (isActive && (shiftNumber === 1 || shiftNumber === 2)) {
            const defaultShiftTab: ShiftTab = shiftNumber === 1 ? 'SHIFT_1' : 'SHIFT_2';
            setActiveShiftTab(defaultShiftTab);
            console.log(`EmployeesPageDB: Set default shift tab to ${defaultShiftTab} based on active shift ${shiftNumber}`);
          }
        }
      } catch (error) {
        console.log('EmployeesPageDB: Could not fetch current shift, using default tab');
      }
    };

    fetchCurrentShift();
  }, [isAppInitialized, apiUrl]);

  const handleOpenModal = (action: ModalActionType, employee?: Employee) => {
    setModalAction(action);
    setSelectedEmployee(employee || null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalAction(null);
    setSelectedEmployee(null);
  };

  const handleAddOrModifyEmployee = async (employeeData: Omit<Employee, 'id'> | Employee, isNew: boolean) => {
    try {
      if (isNew) {
        // Create new employee in database
        const response = await fetch(`${apiUrl}/employees`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(employeeData)
        });
        
        if (response.ok) {
          const result = await response.json();
          const newEmployee = result.data || result;
          setEmployees(prev => [newEmployee, ...prev]);
        }
      } else {
        // Update existing employee in database
        const response = await fetch(`${apiUrl}/employees/${(employeeData as Employee).id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(employeeData)
        });
        
        if (response.ok) {
          const result = await response.json();
          const updatedEmployee = result.data || result;
          setEmployees(prev => prev.map(emp => emp.id === updatedEmployee.id ? updatedEmployee : emp));
        }
      }
    } catch (error) {
      console.error('Error saving employee:', error);
    }
    handleCloseModal();
  };

  const handleDeleteEmployee = async () => {
    if (selectedEmployee) {
      try {
        const response = await fetch(`${apiUrl}/employees/${selectedEmployee.id}`, {
          method: 'DELETE'
        });
        
        if (response.ok) {
          setEmployees(prev => prev.filter(e => e.id !== selectedEmployee.id));
        }
      } catch (error) {
        console.error('Error deleting employee:', error);
      }
    }
    handleCloseModal();
  };

  const handleDeactivateEmployee = (employeeId: string) => {
    const employee = employees.find(e => e.id === employeeId);
    if (employee) {
      handleAddOrModifyEmployee({
        ...employee,
        status: EmployeeStatus.SUSPENDED
      }, false);
    }
  };

  const handleActivateEmployee = (employeeId: string) => {
    const employee = employees.find(e => e.id === employeeId);
    if (employee) {
      handleAddOrModifyEmployee({
        ...employee,
        status: EmployeeStatus.ACTIVE
      }, false);
    }
  };

  // Filtered employees based on search term and shift
  const filteredEmployees = useMemo(() => {
    const shift: ShiftAssignment = activeShiftTab === 'SHIFT_1' ? ShiftAssignment.SHIFT_1 : ShiftAssignment.SHIFT_2;
    
    return employees.filter(emp => {
      const fullName = `${emp.firstName} ${emp.lastName}`.toLowerCase();
      const matchesSearch = fullName.includes(searchTerm.toLowerCase()) ||
        emp.employeeId.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesShift = emp.shiftAssignment === shift || emp.shiftAssignment === ShiftAssignment.BOTH_SHIFTS;
      
      return matchesSearch && matchesShift;
    });
  }, [employees, searchTerm, activeShiftTab]);

  const existingEmployeeNumbers = employees.map(e => e.employeeId);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center gap-2 mb-6">
        <h1 className="text-3xl font-bold text-slate-800">Employees Management</h1>
        <HelpIcon section="employees/managing-employees" tooltip="Learn about employee management" />
      </div>

      {/* Shift Tabs */}
      <div className="flex space-x-2 mb-6">
        <button
          onClick={() => setActiveShiftTab('SHIFT_1')}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            activeShiftTab === 'SHIFT_1'
              ? 'bg-indigo-100 text-indigo-700 ring-2 ring-indigo-500'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          } ${currentShift === 1 ? 'border-2 border-green-400' : ''}`}
        >
          Shift 1 {currentShift === 1 && <span className="ml-2 text-green-600">●</span>}
        </button>
        <button
          onClick={() => setActiveShiftTab('SHIFT_2')}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            activeShiftTab === 'SHIFT_2'
              ? 'bg-indigo-100 text-indigo-700 ring-2 ring-indigo-500'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          } ${currentShift === 2 ? 'border-2 border-green-400' : ''}`}
        >
          Shift 2 {currentShift === 2 && <span className="ml-2 text-green-600">●</span>}
        </button>
      </div>

      {/* Search and Add Employee */}
      <div className="mb-6 flex justify-between items-center">
        <input
          type="text"
          placeholder="Search employees..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="px-4 py-2 border rounded-lg w-64"
        />
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleOpenModal(ModalActionType.ADD)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center"
          >
            <PlusCircleIcon className="w-5 h-5 mr-2" />
            Add Employee
          </button>
          <HelpIcon section="employees/adding-employees" tooltip="Learn how to add employees" />
        </div>
      </div>

      {/* Employee Table */}
      {loadingEmployees ? (
        <div className="text-center py-8">Loading employees...</div>
      ) : (
        <EmployeeTable
          employees={filteredEmployees}
          onModify={(emp) => handleOpenModal(ModalActionType.MODIFY, emp)}
          onDelete={(emp) => handleOpenModal(ModalActionType.DELETE, emp)}
          onDeactivate={handleDeactivateEmployee}
          onActivate={handleActivateEmployee}
        />
      )}

      {/* Modals */}
      {modalAction === ModalActionType.ADD && (
        <EmployeeFormModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSubmit={handleAddOrModifyEmployee}
          title="Add New Employee"
          existingEmployeeNumbers={existingEmployeeNumbers}
        />
      )}
      {modalAction === ModalActionType.MODIFY && selectedEmployee && (
        <EmployeeFormModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSubmit={handleAddOrModifyEmployee}
          initialData={selectedEmployee}
          title="Edit Employee"
          existingEmployeeNumbers={existingEmployeeNumbers}
        />
      )}
      {modalAction === ModalActionType.DELETE && selectedEmployee && (
        <DeleteEmployeeModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onConfirm={handleDeleteEmployee}
          employee={selectedEmployee}
        />
      )}
    </div>
  );
};

export default EmployeesPageDB;